import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { projectsApi } from "../api/projects"
import type { Project, ProjectFormValues, CostCodeBudget } from "./types"

// Query keys
export const projectKeys = {
  all: ["projects"] as const,
  lists: () => [...projectKeys.all, "list"] as const,
  list: (filters: string) => [...projectKeys.lists(), { filters }] as const,
  details: () => [...projectKeys.all, "detail"] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
}

// Hook to fetch all projects
export const useProjects = () => {
  return useQuery({
    queryKey: projectKeys.lists(),
    queryFn: projectsApi.getProjects,
  })
}

// Hook to fetch a single project
export const useProject = (id: string) => {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => projectsApi.getProject(id),
    enabled: !!id,
  })
}

// Hook to create a project
export const useCreateProject = () => {
  const queryClient = useQueryClient()

  return useMutation<Project, Error, ProjectFormValues>({
    mutationFn: (project: ProjectFormValues) => projectsApi.createProject(project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
    },
  })
}

// Hook to update a project
export const useUpdateProject = (id: string) => {
  const queryClient = useQueryClient()

  return useMutation<Project, Error, Partial<ProjectFormValues>>({
    mutationFn: (project: Partial<ProjectFormValues>) => projectsApi.updateProject(id, project),
    onSuccess: (updatedProject) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })

      // Update the project in the cache
      queryClient.setQueryData(projectKeys.detail(id), updatedProject)

      // Update the project in the list cache
      queryClient.setQueryData(projectKeys.lists(), (old: Project[] | undefined) => {
        if (!old) return old
        return old.map((project) => (project._id === id ? updatedProject : project))
      })
    },
  })
}

// Hook to delete a project
export const useDeleteProject = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => projectsApi.deleteProject(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
      queryClient.removeQueries({ queryKey: projectKeys.detail(id) })
    },
  })
}

// Hook to update cost code budgets
export const useUpdateCostCodeBudgets = (projectId: string) => {
  const queryClient = useQueryClient()

  return useMutation<Project, Error, CostCodeBudget[]>({
    mutationFn: (costCodeBudgets) => projectsApi.updateCostCodeBudgets(projectId, costCodeBudgets),
    onSuccess: (updatedProject) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) })

      // Update the project in the cache
      queryClient.setQueryData(projectKeys.detail(projectId), updatedProject)
    },
  })
}

// Hook to remove a seat from a project
export const useRemoveSeatFromProject = (projectId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (seatId: string) => projectsApi.removeSeatFromProject(projectId, seatId),
    onSuccess: (updatedProject) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) })

      // Update the project in the cache
      queryClient.setQueryData(projectKeys.detail(projectId), updatedProject)
      // Refresh available seats and personnel lists
      queryClient.invalidateQueries({ queryKey: ['seats'] })
      queryClient.invalidateQueries({ queryKey: ['personnel'] })
    },
  })
}

// Hook to attach a seat to a project
export const useAttachSeatToProject = (projectId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ seatId, personnelId }: { seatId: string; personnelId: string }) =>
      projectsApi.attachSeatToProject(projectId, seatId, personnelId),
    onSuccess: (updatedProject) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) })

      // Update the project in the cache
      queryClient.setQueryData(projectKeys.detail(projectId), updatedProject)
    },
  })
}
